import type { MenuItem, TpaPageLink } from '@wix/document-services-types';
import { HttpClient } from '@wix/http-client';
import type { ComponentRef, EditorSDK } from '@wix/platform-editor-sdk';

import { getSiteLocale } from '../../../../utils/locale';
import { APP_TOKEN, MENU_IDS, SANTA_MEMBERS_APP_ID } from '../../../constants';
import { monitoredNavigateToMemberSettingsPage } from '../../../platform-app/split-ma/editor-ready-utils/monitored-methods';
import { connectLoginButton } from '../../../wrappers/components';
import { setIsMigration } from '../../applicationState';

type WidgetTitle = {
  title: string;
  widgetId: string;
};

type WidgetTitles = {
  appDefinitionId: string;
  widgets: WidgetTitle[];
};

const LOGIN_BAR_COMP_TYPE = 'wysiwyg.viewer.components.LoginSocialBar';

const uninstallMembersAreaV1 = async (editorSDK: EditorSDK) => {
  await editorSDK.application.uninstall(APP_TOKEN, {
    openConfirmation: false,
  });
};

const installMembersAreaV3 = async (editorSDK: EditorSDK) => {
  await editorSDK.application.install(APP_TOKEN, {
    appDefinitionId: SANTA_MEMBERS_APP_ID,
    originInfo: {
      migrationData: {
        isMigrating: true,
      },
    },
  });
};

const getLoginBarComponentRef = async (editorSDK: EditorSDK): Promise<ComponentRef | undefined> => {
  const loginBarRefs = await editorSDK.components.findAllByType(APP_TOKEN, {
    componentType: LOGIN_BAR_COMP_TYPE,
  });

  return loginBarRefs[0];
};

const getLoginBarControllerRef = async (
  editorSDK: EditorSDK,
  loginBarRef: ComponentRef,
): Promise<ComponentRef | undefined> => {
  const connections = await editorSDK.controllers.listConnections(APP_TOKEN, {
    componentRef: loginBarRef,
  });

  return connections[0]?.controllerRef;
};

const disconnectLoginBar = async (editorSDK: EditorSDK) => {
  const loginBarRef = await getLoginBarComponentRef(editorSDK);

  if (!loginBarRef) {
    throw new Error('[MA v3 migration]: Login bar component ref not found');
  }

  const controllerRef = await getLoginBarControllerRef(editorSDK, loginBarRef);

  if (!controllerRef) {
    throw new Error('[MA v3 migration]: Login bar controller ref not found');
  }

  await editorSDK.components.data.update(APP_TOKEN, {
    componentRef: loginBarRef,
    data: {
      iconItemsRef: null,
      menuItemsRef: null,
    },
  });

  await editorSDK.components.data.update(APP_TOKEN, {
    componentRef: controllerRef,
    data: {
      applicationId: 'placeholder',
    },
  });
};

const reconnectLoginBar = async (editorSDK: EditorSDK) => {
  const loginBarRef = await getLoginBarComponentRef(editorSDK);

  if (!loginBarRef) {
    throw new Error('[MA v3 migration]: Login bar component ref not found');
  }

  const controllerRef = await getLoginBarControllerRef(editorSDK, loginBarRef);

  const shouldConnectToGlobalController = !controllerRef;

  await connectLoginButton(editorSDK, loginBarRef, shouldConnectToGlobalController);

  if (!shouldConnectToGlobalController) {
    await editorSDK.components.data.update(APP_TOKEN, {
      componentRef: controllerRef,
      data: {
        applicationId: SANTA_MEMBERS_APP_ID,
      },
    });
  }
};

const getWidgetTitles = async (siteLocale: string) => {
  const translationsUrl = `https://editor.wix.com/_api/app-market-api/apps/page-apps?fields=widgets.title,widgets.widgetId,appDefinitionId&lang=${siteLocale}`;
  const httpClient = new HttpClient();
  const { data } = await httpClient.get<WidgetTitles[]>(translationsUrl);

  return data;
};

const getMenus = async (editorSDK: EditorSDK) => {
  return Promise.all([
    editorSDK.menu.getById(APP_TOKEN, { menuId: MENU_IDS.SUB_MENU_ID }),
    editorSDK.menu.getById(APP_TOKEN, { menuId: MENU_IDS.SETTINGS_SUB_MENU_ID }),
    editorSDK.menu.getById(APP_TOKEN, { menuId: MENU_IDS.LOGIN_MENU_ID }),
  ]);
};

const getTranslatedMenuItems = async (editorSDK: EditorSDK, menuItems: MenuItem[], widgetTitles: WidgetTitles[]) => {
  const translatedMenuItems: MenuItem[] = [];

  menuItems.forEach((item) => {
    let translatedLabel = '';

    for (const app of widgetTitles) {
      const widgetTitle = app.widgets.find(({ widgetId }) => widgetId === (item.link as TpaPageLink)?.itemId);

      if (widgetTitle) {
        translatedLabel = widgetTitle.title;
        break;
      }
    }

    translatedMenuItems.push({
      ...item,
      label: translatedLabel || item.label,
    });
  });

  return translatedMenuItems;
};

const updateMenusWithTranslatedItems = async (editorSDK: EditorSDK) => {
  const siteLocale = await getSiteLocale(editorSDK);

  if (siteLocale === 'en') {
    return;
  }

  const widgetTitles = await getWidgetTitles(siteLocale);
  const menus = await getMenus(editorSDK);

  await Promise.all(
    menus.map(async (menu) => {
      const translatedMenuItems = await getTranslatedMenuItems(editorSDK, menu.items, widgetTitles);

      await editorSDK.menu.update(APP_TOKEN, {
        menuId: menu.id,
        menuData: {
          items: translatedMenuItems,
        },
      });
    }),
  );
};

export const migrateToV3OnTemplates = async (editorSDK: EditorSDK) => {
  try {
    setIsMigration(true);
    console.log('Members Area: Starting migration to V3');

    await disconnectLoginBar(editorSDK);

    await uninstallMembersAreaV1(editorSDK);
    await installMembersAreaV3(editorSDK);

    await reconnectLoginBar(editorSDK);

    await updateMenusWithTranslatedItems(editorSDK);
    await monitoredNavigateToMemberSettingsPage(editorSDK);

    console.log('Members Area: Migration to V3 finished');
  } catch (e) {
    throw e;
  } finally {
    setIsMigration(false);
  }
};
